import {
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react"
import { getTurnstileTheme, useTurnstileReady } from "source/shared/turnstile"

export interface TurnstileWidgetRef {
  execute: () => Promise<string | null>
  reset: () => void
}

export interface TurnstileWidgetProps {
  siteKey: string | null | undefined
  action: string
  enabled?: boolean
}

export type TurnstileExecuteErrorCode =
  | "superseded"
  | "reset"
  | "unmounted"
  | "challenge_failed"
  | "timeout"
  | "unsupported"

export class TurnstileExecuteError extends Error {
  readonly code: TurnstileExecuteErrorCode

  constructor(code: TurnstileExecuteErrorCode) {
    super(`Turnstile execute: ${code}`)
    this.name = "TurnstileExecuteError"
    this.code = code
  }
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: Record<string, unknown>,
      ) => string | undefined
      execute: (widgetId: string) => void
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

// Budget for the challenge to download and resolve silently before we give
// up. Sized to cover slow connections and slower devices, whose CPU-bound
// challenge takes longer to resolve, so they don't time out prematurely.
export const SILENT_TIMEOUT_MS = 20_000
export const INTERACTIVE_TIMEOUT_MS = 30_000

interface PendingExecution {
  resolve: (token: string | null) => void
  reject: (reason?: unknown) => void
  startedAt: number
  interactive: boolean
}

function TurnstileWidget(
  { siteKey, action, enabled: enabledProp = true }: TurnstileWidgetProps,
  ref: Ref<TurnstileWidgetRef>,
) {
  const enabled = enabledProp && !!siteKey
  const turnstileReady = useTurnstileReady(enabled)
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const pendingRef = useRef<PendingExecution | null>(null)
  const backstopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const settlePending = useCallback(() => {
    pendingRef.current = null
    if (backstopTimerRef.current !== null) {
      clearTimeout(backstopTimerRef.current)
      backstopTimerRef.current = null
    }
  }, [])

  const recordOutcome = useCallback(
    (outcome: string) => {
      const pending = pendingRef.current
      const context = {
        action,
        outcome,
        interactive: pending?.interactive ?? false,
        duration_ms: pending ? Date.now() - pending.startedAt : null,
      }
      import("@datadog/browser-rum").then(({ datadogRum }) =>
        datadogRum.addAction("turnstile.execute_outcome", context),
      )
      // RUM samples ~12% of sessions; log rejections separately at 100% so
      // individual reports can be debugged. Excludes "token" (success) and
      // "no_widget" (fails open, no error shown) since only rejections are
      // errors.
      if (outcome !== "token" && outcome !== "no_widget") {
        import("@datadog/browser-logs").then(({ datadogLogs }) =>
          datadogLogs.logger.error("turnstile.execute_outcome", context),
        )
      }
    },
    [action],
  )

  const startBackstop = useCallback(
    (ms: number) => {
      if (backstopTimerRef.current !== null) {
        clearTimeout(backstopTimerRef.current)
      }
      backstopTimerRef.current = setTimeout(() => {
        const pending = pendingRef.current
        if (pending) recordOutcome("timeout_client")
        settlePending()
        if (pending) pending.reject(new TurnstileExecuteError("timeout"))
      }, ms)
    },
    [recordOutcome, settlePending],
  )

  useImperativeHandle(
    ref,
    () => ({
      execute: () => {
        if (!enabled) return Promise.resolve(null)
        // If the script/widget hasn't loaded yet, fall back to a null token
        // and let the backend killswitch decide — matches the contract in
        // source/shared/turnstile.js.
        if (!widgetIdRef.current || !window.turnstile) {
          recordOutcome("no_widget")
          return Promise.resolve(null)
        }
        if (pendingRef.current) {
          recordOutcome("superseded")
          pendingRef.current.reject(new TurnstileExecuteError("superseded"))
          settlePending()
        }
        const promise = new Promise<string | null>((resolve, reject) => {
          pendingRef.current = {
            resolve,
            reject,
            startedAt: Date.now(),
            interactive: false,
          }
        })
        window.turnstile.execute(widgetIdRef.current)
        startBackstop(SILENT_TIMEOUT_MS)
        return promise
      },
      reset: () => {
        if (pendingRef.current) {
          recordOutcome("reset")
          pendingRef.current.reject(new TurnstileExecuteError("reset"))
          settlePending()
        }
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current)
        }
      },
    }),
    [enabled, recordOutcome, settlePending, startBackstop],
  )

  useEffect(() => {
    if (!enabled || !turnstileReady || !containerRef.current) return

    const widgetId = window.turnstile!.render(containerRef.current, {
      sitekey: siteKey!,
      action,
      size: "flexible",
      theme: getTurnstileTheme(),
      execution: "execute",
      appearance: "interaction-only",
      callback: (token: string) => {
        const pending = pendingRef.current
        if (pending) recordOutcome("token")
        settlePending()
        if (pending) pending.resolve(token)
      },
      "error-callback": () => {
        const pending = pendingRef.current
        if (pending) recordOutcome("challenge_failed")
        settlePending()
        if (pending)
          pending.reject(new TurnstileExecuteError("challenge_failed"))
      },
      "expired-callback": () => {
        // No-op: the next execute() will start a fresh challenge.
      },
      "timeout-callback": () => {
        const pending = pendingRef.current
        if (pending) recordOutcome("timeout_cf")
        settlePending()
        if (pending) pending.reject(new TurnstileExecuteError("timeout"))
      },
      "unsupported-callback": () => {
        const pending = pendingRef.current
        if (pending) recordOutcome("unsupported")
        settlePending()
        if (pending) pending.reject(new TurnstileExecuteError("unsupported"))
      },
      "before-interactive-callback": () => {
        const pending = pendingRef.current
        if (!pending) return
        pending.interactive = true
        startBackstop(INTERACTIVE_TIMEOUT_MS)
      },
    })

    if (widgetId) {
      widgetIdRef.current = widgetId
    }

    return () => {
      if (pendingRef.current) {
        recordOutcome("unmounted")
        pendingRef.current.reject(new TurnstileExecuteError("unmounted"))
        settlePending()
      }
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [
    enabled,
    turnstileReady,
    siteKey,
    action,
    recordOutcome,
    settlePending,
    startBackstop,
  ])

  if (!enabled) return null

  return <div ref={containerRef} data-testid="turnstile" />
}

export default forwardRef(TurnstileWidget)
